import React, {
  useCallback,
  useEffect,
  forwardRef,
  HTMLAttributes,
  useState,
  CSSProperties,
  MouseEvent,
  useImperativeHandle,
} from "react";
import { Transition } from "react-transition-group";

import classnames from "classnames";

import {
  StyledAnimatedSpan,
  StyledCssTransition,
  StyledHeader,
  StyledIconsWrapperSmall,
  StyledListSeparator,
  StyledSheetIcons,
  StyledSheetWrapper,
  StyledSheetWrapperPaper,
} from "./Styles";
import { IconButton } from "../IconButton";
import { Overlay } from "../Overlay";
import { Tooltip } from "../Tooltip";
import { Window } from "../Window";

interface ControlProps {
  onClick: () => void;
  icon: React.ReactElement;
  tooltip: string;
  disabled: boolean;
  className: string;
}

interface ISheet {
  onClose?: () => void;
  controls?: ControlProps[];
  open?: boolean;
  animation?: "top" | "bottom" | "left" | "right";
  position?: "center" | "left" | "right";
  mode?: "normal" | "stretch";
  disableFocusLock?: boolean;
  disableScrollLock?: boolean;
  disableBackgroundColor?: boolean;
  bodyStyle?: CSSProperties;
  bodyClassName?: string;
  disableBackgroundClick?: boolean;
}

export interface SheetMethods {
  close: () => void;
}

type SheetProps = ISheet & HTMLAttributes<HTMLDivElement>;

export const Sheet = forwardRef<SheetMethods, SheetProps>(
  (
    {
      children,
      onClose,
      controls = [],
      open: defaultOpen = false,
      animation = "top",
      position = "center",
      mode = "normal",
      disableFocusLock = false,
      disableScrollLock = false,
      disableBackgroundColor = false,
      bodyStyle,
      bodyClassName,
      disableBackgroundClick = false,
      ...rest
    },
    ref
  ) => {
    const [open, setOpen] = useState(defaultOpen);
    const [exited, setExited] = useState(true);

    useEffect(() => {
      setOpen(defaultOpen);
    }, [defaultOpen]);

    const handleEnter = useCallback(() => {
      setExited(false);
    }, []);

    const handleExited = useCallback(() => {
      setExited(true);
      onClose?.();
    }, [onClose]);

    const handleClose = useCallback(() => {
      setOpen(false);
    }, []);

    const handleBackgroundClick = useCallback(
      (event: MouseEvent<HTMLDivElement>) => {
        if (!disableBackgroundClick) {
          event.preventDefault();
          handleClose();
        }
      },
      [disableBackgroundClick, handleClose]
    );

    useImperativeHandle(ref, () => ({
      close: handleClose,
    }));

    if (!open && exited) {
      return null;
    }

    return (
      <Window
        {...rest}
        onClose={handleClose}
        disableFocusLock={disableFocusLock}
        disableScrollLock={disableScrollLock}
      >
        {open && (
          <Overlay
            onClick={handleBackgroundClick}
            disableBackgroundColor={disableBackgroundColor}
          />
        )}
        <StyledSheetWrapper
          className={classnames("c-sheet__wrapper", bodyClassName)}
          style={bodyStyle}
          $position={position}
          $mode={mode}
        >
          {controls.length > 0 && (
            <StyledSheetIcons>
              {controls.map(
                (
                  {
                    icon: IconComponent,
                    tooltip,
                    onClick,
                    disabled,
                    className,
                  },
                  index
                ) => {
                  const maxDurationIn = 4;
                  const maxDurationOut = 2;
                  const control = index + 1;
                  const durationOut = Math.max(0, maxDurationOut - control);
                  const durationIn = Math.min(control, maxDurationIn);

                  return (
                    <Transition
                      appear
                      in={open}
                      timeout={maxDurationIn * 100}
                      key={`c-sheet-control-${index}`}
                    >
                      {(state) => (
                        <Tooltip title={tooltip} disable={!tooltip || disabled}>
                          <StyledAnimatedSpan
                            onClick={onClick}
                            $disabled={disabled}
                            $mode={mode}
                            $state={state}
                            className={className}
                            $maxDurationIn={maxDurationIn / 10}
                            $maxDurationOut={maxDurationOut / 10}
                            $durationOut={durationOut / 10}
                            $durationIn={durationIn / 10}
                          >
                            {IconComponent}
                          </StyledAnimatedSpan>
                        </Tooltip>
                      )}
                    </Transition>
                  );
                }
              )}
            </StyledSheetIcons>
          )}
          <StyledCssTransition
            appear
            in={open}
            timeout={400}
            classNames="c-sheet__animation"
            $direction={animation}
            onEnter={handleEnter}
            onExited={handleExited}
          >
            <StyledSheetWrapperPaper paperClass="c-sheet__wrapper__paper">
              {controls.length > 0 && (
                <StyledHeader>
                  <StyledIconsWrapperSmall>
                    {controls.map(
                      (
                        {
                          icon: IconComponent,
                          tooltip,
                          onClick,
                          disabled,
                          className,
                        },
                        index
                      ) => (
                        <Tooltip
                          title={tooltip}
                          disable={!tooltip || disabled}
                          key={index}
                        >
                          <IconButton
                            onClick={onClick}
                            disabled={disabled}
                            className={className}
                            variant="text gray"
                          >
                            {IconComponent}
                          </IconButton>
                        </Tooltip>
                      )
                    )}
                  </StyledIconsWrapperSmall>
                  <StyledListSeparator />
                </StyledHeader>
              )}
              {children}
            </StyledSheetWrapperPaper>
          </StyledCssTransition>
        </StyledSheetWrapper>
      </Window>
    );
  }
);

Sheet.displayName = "Sheet";
